// Stage 73.20cr (Phase D) — Consolidated Monthly Payroll Input Approval.
//
// Single-page module with:
//   • Cycle picker (Financial Year · Payroll Month · Company)
//   • Sidebar of 26 category tabs (auto-badged with row counts)
//   • Summary cards at the top
//   • Data table per tab with row-level checkboxes + reason chips + docs
//   • Workflow chain visualiser (5 configurable levels)
//   • Approve / Reject / Send Back / Clarify buttons (whole cycle
//     OR per-category, per config)
//   • Audit history side panel
//   • CSV export per category
//   • Locking toggle in a small "Settings" popover
//
// Route: /consolidated-approval
// RBAC: Only approver / admin roles can act; the whole page is
// gated at the backend.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';
import {
  UserPlus, UserMinus, PauseCircle, PlayCircle, TrendingUp,
  ArrowUpCircle, ArrowUpDown, Building2, Briefcase, MapPin, Clock,
  ClipboardCheck, CalendarX, ClipboardMinus, IndianRupee, Gift, Zap,
  Wallet, HandCoins, ScrollText, ShieldCheck, CreditCard, Landmark,
  FileText, CheckCircle2, XCircle, RotateCcw, HelpCircle, Download,
  Settings2, Lock, Unlock, RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { code: 'new_joiner',           label: 'New Joiners',              icon: UserPlus },
  { code: 'resignee',              label: 'Resignees',                icon: UserMinus },
  { code: 'employee_hold',         label: 'Employee Hold',            icon: PauseCircle },
  { code: 'hold_release',          label: 'Hold Release',             icon: PlayCircle },
  { code: 'salary_increment',      label: 'Salary Increment',         icon: TrendingUp },
  { code: 'salary_revision',       label: 'Salary Revision',          icon: ArrowUpCircle },
  { code: 'promotion',             label: 'Promotion',                icon: ArrowUpDown },
  { code: 'transfer',              label: 'Transfer',                 icon: MapPin },
  { code: 'department_change',     label: 'Department Change',        icon: Building2 },
  { code: 'designation_change',    label: 'Designation Change',       icon: Briefcase },
  { code: 'cost_center_change',    label: 'Cost Center Change',       icon: Landmark },
  { code: 'shift_change',          label: 'Shift Changes',            icon: Clock },
  { code: 'attendance_correction', label: 'Attendance Corrections',   icon: ClipboardCheck },
  { code: 'leave_adjustment',      label: 'Leave Adjustments',        icon: CalendarX },
  { code: 'lop_adjustment',        label: 'LOP Adjustments',          icon: ClipboardMinus },
  { code: 'arrear',                label: 'Arrear Entries',           icon: IndianRupee },
  { code: 'bonus',                 label: 'Bonus',                    icon: Gift },
  { code: 'incentive',             label: 'Incentives',               icon: Zap },
  { code: 'variable_pay',          label: 'Variable Pay',             icon: Wallet },
  { code: 'reimbursement',         label: 'Reimbursements',           icon: HandCoins },
  { code: 'advance',               label: 'Advances',                 icon: IndianRupee },
  { code: 'loan_change',           label: 'Advance Changes',             icon: ScrollText },
  { code: 'statutory_change',      label: 'Statutory Changes',        icon: ShieldCheck },
  { code: 'bank_detail_change',    label: 'Bank Detail Changes',      icon: CreditCard },
  { code: 'pf_esi_change',         label: 'PF / ESI Changes',         icon: ShieldCheck },
  { code: 'tax_declaration_change',label: 'Tax Declaration Changes',  icon: FileText },
];

function _statusBadge(s) {
  const map = {
    draft: 'bg-slate-100 text-slate-700',
    in_review: 'bg-blue-100 text-blue-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    sent_back: 'bg-amber-100 text-amber-700',
  };
  return `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[s] || 'bg-slate-100 text-slate-600'}`;
}

export default function ConsolidatedApproval() {
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [selMonth, setSelMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [selFy, setSelFy] = useState('2025-26');
  const [cycle, setCycle] = useState(null);
  const [summary, setSummary] = useState(null);
  const [workflowCfg, setWorkflowCfg] = useState(null);
  const [activeTab, setActiveTab] = useState('new_joiner');
  const [tabData, setTabData] = useState({ rows: [], count: 0, approval_state: {} });
  const [loading, setLoading] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [lockingEnabled, setLockingEnabled] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(r.data || []);
        if (r.data?.length) setSelCompany(r.data[0].id);
      } catch (e) { console.error(e); }
    })();
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/consolidated-approval/tenant-settings`, { withCredentials: true });
        setLockingEnabled(!!r.data?.consolidated_approval_required);
      } catch (e) { /* ignore */ }
    })();
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/consolidated-approval/workflow-config`, { withCredentials: true });
        setWorkflowCfg(r.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const loadCycle = useCallback(async () => {
    if (!selCompany || !selMonth) return;
    setLoading(true);
    try {
      // Try list first, else create.
      const list = await axios.get(
        `${API_URL}/consolidated-approval/cycles?company_id=${selCompany}&payroll_month=${selMonth}`,
        { withCredentials: true },
      );
      let cy = (list.data || [])[0];
      if (!cy) {
        const created = await axios.post(
          `${API_URL}/consolidated-approval/cycles`,
          { company_id: selCompany, financial_year: selFy, payroll_month: selMonth },
          { withCredentials: true },
        );
        cy = created.data;
      }
      setCycle(cy);
      const sum = await axios.get(`${API_URL}/consolidated-approval/cycles/${cy.id}/summary`,
                                    { withCredentials: true });
      setSummary(sum.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load cycle.');
    }
    setLoading(false);
  }, [selCompany, selMonth, selFy]);

  useEffect(() => { loadCycle(); }, [loadCycle]);

  // Load tab data whenever activeTab or cycle changes
  useEffect(() => {
    if (!cycle) return;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/consolidated-approval/cycles/${cycle.id}/tab/${activeTab}`,
                                    { withCredentials: true });
        setTabData(r.data);
      } catch (e) { console.error(e); }
    })();
  }, [activeTab, cycle]);

  const loadHistory = async () => {
    if (!cycle) return;
    const r = await axios.get(`${API_URL}/consolidated-approval/cycles/${cycle.id}/history`,
                               { withCredentials: true });
    setHistory(r.data.history || []);
    setShowHistory(true);
  };

  const performAction = async (action, scope = 'cycle') => {
    if (!cycle) return;
    try {
      const r = await axios.post(`${API_URL}/consolidated-approval/cycles/${cycle.id}/action`,
                                   { action, scope, category: scope === 'category' ? activeTab : null, remarks },
                                   { withCredentials: true });
      setCycle(r.data);
      setRemarks('');
      loadCycle();  // refresh summary
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed.');
    }
  };

  const toggleLocking = async () => {
    try {
      const r = await axios.put(`${API_URL}/consolidated-approval/tenant-settings`,
                                  { consolidated_approval_required: !lockingEnabled },
                                  { withCredentials: true });
      setLockingEnabled(!!r.data.consolidated_approval_required);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const currentLevel = useMemo(() => {
    const levels = (cycle?.workflow_snapshot?.levels) || (workflowCfg?.levels) || [];
    return levels.find((l) => l.order === cycle?.current_level) || null;
  }, [cycle, workflowCfg]);

  const activeCategoryLabel = CATEGORIES.find((c) => c.code === activeTab)?.label || activeTab;

  // Export CSV
  const exportCsv = () => {
    if (!cycle) return;
    window.open(`${API_URL}/consolidated-approval/cycles/${cycle.id}/export/${activeTab}.csv`, '_blank');
  };

  return (
    <MainLayout>
    <div data-testid="consolidated-approval-page" className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Consolidated Monthly Payroll Input Approval</h1>
          <p className="text-sm text-slate-500 mt-1">
            Review all payroll-impacting changes for the month before payroll processing.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button data-testid="settings-btn" onClick={() => setShowSettings(!showSettings)}
                  className="p-2 hover:bg-slate-100 rounded-md">
            <Settings2 className="w-4 h-4 text-slate-600" />
          </button>
          <button data-testid="history-btn" onClick={loadHistory}
                  className="text-sm px-3 py-1.5 border rounded-md hover:bg-slate-50">
            View History
          </button>
          <button data-testid="refresh-btn" onClick={loadCycle}
                  className="text-sm px-3 py-1.5 border rounded-md hover:bg-slate-50 flex items-center space-x-1">
            <RefreshCw className="w-3.5 h-3.5" /><span>Refresh</span>
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-800">Locking</p>
            <p className="text-xs text-slate-500">
              When enabled, payroll processing is blocked until this cycle is fully approved.
            </p>
          </div>
          <button data-testid="toggle-locking" onClick={toggleLocking}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${
                    lockingEnabled ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                  }`}>
            {lockingEnabled ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            <span>{lockingEnabled ? 'Locking ON' : 'Locking OFF (default)'}</span>
          </button>
        </div>
      )}

      {/* Cycle picker */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Company</label>
          <select data-testid="cycle-company" value={selCompany} onChange={(e) => setSelCompany(e.target.value)}
                  className="w-full border rounded px-3 py-1.5 text-sm">
            <option value="">Select…</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Financial Year</label>
          <select data-testid="cycle-fy" value={selFy} onChange={(e) => setSelFy(e.target.value)}
                  className="w-full border rounded px-3 py-1.5 text-sm">
            {['2024-25', '2025-26', '2026-27', '2027-28'].map((f) => <option key={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Payroll Month</label>
          <input data-testid="cycle-month" type="month" value={selMonth}
                 onChange={(e) => setSelMonth(e.target.value)}
                 className="w-full border rounded px-3 py-1.5 text-sm" />
        </div>
        <div className="flex items-end">
          <div className="text-sm">
            <p className="text-xs text-slate-500">Cycle Status</p>
            <span data-testid="cycle-status-badge" className={_statusBadge(cycle?.status)}>
              {(cycle?.status || 'not_created').replace('_', ' ')}
            </span>
            {currentLevel && cycle?.status === 'in_review' && (
              <span className="ml-2 text-xs text-slate-500">Awaiting: <b>{currentLevel.name}</b></span>
            )}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-6 gap-2">
          {[
            { label: 'Total Records', value: summary.total_records, color: 'bg-blue-50 text-blue-700' },
            { label: 'Approved', value: summary.approved_count, color: 'bg-emerald-50 text-emerald-700' },
            { label: 'Rejected', value: summary.rejected_count, color: 'bg-red-50 text-red-700' },
            { label: 'Sent Back', value: summary.sent_back_count, color: 'bg-amber-50 text-amber-700' },
            { label: 'New Joiners', value: summary.counts?.new_joiner || 0, color: 'bg-slate-50 text-slate-700' },
            { label: 'Resignees', value: summary.counts?.resignee || 0, color: 'bg-slate-50 text-slate-700' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className={`text-lg font-bold ${s.color.split(' ')[1]}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Workflow chain */}
      {workflowCfg?.levels && (
        <div className="bg-white rounded-xl border border-slate-200 p-3">
          <p className="text-xs font-medium text-slate-500 mb-2">Approval Workflow</p>
          <div className="flex items-center space-x-2 overflow-x-auto">
            {workflowCfg.levels.map((l, i) => {
              const isCurrent = cycle?.current_level === l.order && cycle?.status === 'in_review';
              const isPast = (cycle?.current_level || 1) > l.order || cycle?.status === 'approved';
              return (
                <React.Fragment key={l.order}>
                  <div className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                    isCurrent ? 'bg-blue-600 text-white' :
                    isPast    ? 'bg-emerald-100 text-emerald-700' :
                                 'bg-slate-100 text-slate-500'
                  }`}>
                    {l.name}
                  </div>
                  {i < workflowCfg.levels.length - 1 && <div className="text-slate-300">→</div>}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Main content — sidebar + table + actions */}
      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar */}
        <div className="col-span-3 bg-white rounded-xl border border-slate-200 p-2 max-h-[600px] overflow-y-auto">
          {CATEGORIES.map((c) => {
            const count = summary?.counts?.[c.code] || 0;
            const isActive = c.code === activeTab;
            return (
              <button key={c.code} data-testid={`tab-${c.code}`}
                      onClick={() => setActiveTab(c.code)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm text-left mb-1 ${
                        isActive ? 'bg-[#0052FF] text-white' : 'hover:bg-slate-50 text-slate-700'
                      }`}>
                <span className="flex items-center space-x-2">
                  <c.icon className="w-4 h-4" />
                  <span>{c.label}</span>
                </span>
                {count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20' : 'bg-slate-200 text-slate-700'
                  }`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Data + actions */}
        <div className="col-span-9 space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">{activeCategoryLabel}</h3>
                <p className="text-xs text-slate-500">{tabData.count} record{tabData.count !== 1 ? 's' : ''}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button data-testid="export-csv-btn" onClick={exportCsv}
                        className="text-xs px-3 py-1.5 border rounded-md hover:bg-slate-50 flex items-center space-x-1">
                  <Download className="w-3.5 h-3.5" /><span>CSV</span>
                </button>
                {cycle?.workflow_snapshot?.allow_category_level_approval && cycle?.status === 'in_review' && (
                  <button data-testid="approve-category-btn"
                          onClick={() => performAction('approve', 'category')}
                          className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-md flex items-center space-x-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /><span>Approve {activeCategoryLabel}</span>
                  </button>
                )}
              </div>
            </div>

            {tabData.rows.length === 0 ? (
              <div className="text-center py-10">
                <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No records for {activeCategoryLabel} in {selMonth}.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500">
                    <tr className="text-left border-b">
                      {Object.keys(tabData.rows[0] || {})
                        .filter((k) => !['id', 'source', 'documents'].includes(k))
                        .map((k) => <th key={k} className="py-2 pr-2 font-medium">{k.replace(/_/g, ' ')}</th>)}
                      <th className="text-right">Docs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tabData.rows.slice(0, 50).map((r, i) => (
                      <tr key={r.id || i} data-testid={`row-${r.id || i}`}>
                        {Object.entries(r)
                          .filter(([k]) => !['id', 'source', 'documents'].includes(k))
                          .map(([k, v]) => (
                            <td key={k} className="py-2 pr-2">
                              {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
                            </td>
                          ))}
                        <td className="text-right">
                          {(r.documents || []).length > 0
                            ? <span className="text-[#0052FF] cursor-pointer">{(r.documents || []).length} doc(s)</span>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {tabData.rows.length > 50 && (
                  <p className="text-xs text-slate-400 mt-2">Showing first 50 of {tabData.rows.length}. Export CSV for full list.</p>
                )}
              </div>
            )}
          </div>

          {/* Cycle-level actions */}
          {cycle?.status === 'in_review' && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Remarks (optional)</label>
                <textarea data-testid="approval-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                          rows={2}
                          className="w-full border rounded px-3 py-1.5 text-sm"
                          placeholder="Add remarks visible in the audit history…" />
              </div>
              <div className="flex items-center flex-wrap gap-2">
                <button data-testid="approve-cycle-btn" onClick={() => performAction('approve', 'cycle')}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-md flex items-center space-x-1">
                  <CheckCircle2 className="w-4 h-4" /><span>Approve at Level {cycle?.current_level}</span>
                </button>
                <button data-testid="reject-cycle-btn" onClick={() => performAction('reject', 'cycle')}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md flex items-center space-x-1">
                  <XCircle className="w-4 h-4" /><span>Reject</span>
                </button>
                <button data-testid="send-back-btn" onClick={() => performAction('send_back', 'cycle')}
                        className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-md flex items-center space-x-1">
                  <RotateCcw className="w-4 h-4" /><span>Send Back</span>
                </button>
                <button data-testid="clarify-btn" onClick={() => performAction('clarify', 'cycle')}
                        className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white text-sm rounded-md flex items-center space-x-1">
                  <HelpCircle className="w-4 h-4" /><span>Request Clarification</span>
                </button>
              </div>
            </div>
          )}

          {cycle?.status === 'approved' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center space-x-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="text-sm text-emerald-800 font-medium">
                Cycle fully approved. Payroll processing can now proceed for this month.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* History side panel */}
      {showHistory && (
        <div data-testid="history-panel" className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl border-l border-slate-200 z-50 overflow-y-auto">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800">Audit History</h3>
            <button onClick={() => setShowHistory(false)} className="text-slate-500 hover:text-slate-800">✕</button>
          </div>
          <div className="p-4 space-y-3">
            {history.length === 0 && <p className="text-xs text-slate-500">No actions yet.</p>}
            {history.map((h, i) => (
              <div key={i} className="border-l-2 border-slate-200 pl-3 pb-3">
                <p className="text-xs font-medium text-slate-800">
                  {h.action?.toUpperCase()} — Level {h.level}
                  {h.scope === 'category' && ` · ${h.category}`}
                </p>
                <p className="text-xs text-slate-500">
                  {h.approver_name || h.approver_email || h.approver_id}
                  {' · '}{h.approver_role}
                </p>
                {h.remarks && <p className="text-xs text-slate-600 mt-1">&ldquo;{h.remarks}&rdquo;</p>}
                <p className="text-xs text-slate-400 mt-0.5">{h.at}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    </MainLayout>
  );
}
